import { useSortable } from "@dnd-kit/sortable";
import { Box, useTheme } from "@mui/material";
import type { Cell, ColumnSizingState, Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { CheckBox } from "components/checkbox";
import type React from "react";

interface TableCellProps<TData> {
  cell: Cell<TData, unknown>;
  enableColumnResizing?: boolean;
  columnSizing?: ColumnSizingState;
  onCheckboxToggle?: (row: Row<TData>, isShiftKey: boolean) => void;
}
export const TableCell = <TData,>({
  cell,
  enableColumnResizing = false,
  columnSizing,
  onCheckboxToggle,
}: TableCellProps<TData>) => {
  const theme = useTheme();
  const meta = cell.column.columnDef.meta;
  const isCheckbox = cell.column.columnDef.header === "select";
  const canResize = enableColumnResizing && cell.column.getCanResize();
  const hasSized =
    canResize &&
    (cell.column.getIsResizing() ||
      (columnSizing != null && cell.column.id in columnSizing));
  const size = hasSized ? cell.column.getSize() : undefined;

  const { isDragging, setNodeRef, transform, transition } = useSortable({
    id: cell.column.id,
  });

  return (
    <Box
      ref={setNodeRef}
      component={"td"}
      key={cell.id}
      onClick={
        meta?.onCellClick
          ? (e: React.MouseEvent) => {
              e.stopPropagation();
              meta.onCellClick?.(cell.row.original);
            }
          : isCheckbox
            ? (e: React.MouseEvent) => e.stopPropagation()
            : undefined
      }
      sx={{
        p: "8px 16px",
        ...theme.typography["body-small"],
        whiteSpace: "nowrap",
        ...(hasSized
          ? {
              width: `${size}px`,
              minWidth: `${size}px`,
              maxWidth: `${size}px`,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
          : {
              minWidth: isCheckbox ? "30px" : "140px",
              maxWidth: isCheckbox ? "70px" : "200px",
              width: isCheckbox ? "70px" : "auto",
              overflowX: "clip",
              textOverflow: "ellipsis",
            }),
        ...(meta?.onCellClick && { cursor: "pointer" }),
        opacity: isDragging ? 0.5 : 1,
        transform: transform
          ? `translate3d(${Math.round(transform.x)}px, 0, 0)`
          : undefined,
        transition: transition ?? undefined,
        zIndex: isDragging ? 1 : 0,
        ...meta?.cellSx,
      }}
    >
      {isCheckbox && (
        <CheckBox
          checked={cell.row.getIsSelected()}
          disabled={!cell.row.getCanSelect()}
          indeterminate={cell.row.getIsSomeSelected()}
          onMouseDown={(e: React.MouseEvent) => {
            // Prevent native text highlighting when range-selecting with Shift
            if (e.shiftKey) e.preventDefault();
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const isShiftKey =
              (event.nativeEvent as MouseEvent).shiftKey ?? false;
            onCheckboxToggle?.(cell.row, isShiftKey);
          }}
        />
      )}
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </Box>
  );
};
